import React, {createContext, useContext, useEffect, useState} from "react";
import {MDText} from "i18n-react";
import {changeUserLanguage} from "../Services/api";
import {UserReactContext} from "./UserContext";
import LoadingSpinner from "../Components/LoadingSpinner/LoadingSpinner";
import {languagesMap, defaultLanguage} from "./ContextLib";
import {get} from "lodash";




export const isLanguageRtl = ({language}) => {
    return ['heIL', 'arIL'].includes(language)
}

/**
 * Validates if a language code is supported
 * @param {string} languageCode - The language code to validate
 * @returns {boolean} True if language is valid and supported
 */
const isValidLanguage = (languageCode) => {
    return languageCode && typeof languageCode === 'string' && languagesMap[languageCode];
};

/**
 * Safely gets a valid language code, fallback to default if invalid
 * @param {string} languageCode - The language code to validate
 * @returns {string} Valid language code or default
 */
const getSafeLanguage = (languageCode) => {
    return isValidLanguage(languageCode) ? languageCode : defaultLanguage;
};

/**
 * Gets all sub-keys and their translations for a parent i18n key
 * Returns a map of canonical field name -> array of all translations (across all languages)
 * Supports alias keys (e.g., 'organizationsAliases' merges into 'organizations')
 * @param {object} params - Function parameters
 * @param {string} params.parentKey - The parent i18n key (e.g., 'importUsersModal.users')
 * @returns {Object} Map of { fieldName: [translation1, translation2, ...] }
 * @example
 * // For parentKey 'importUsersModal.users' with:
 * // organizations: 'Organizational Units'
 * // organizationsAliases: ['Organizations', 'Units']
 * // Returns:
 * // {
 * //   firstName: ['First Name', 'first name', 'שם פרטי', ...],
 * //   organizations: ['Organizational Units', 'organizational units', 'Organizations', 'organizations', ...]
 * // }
 */
export const getAllTranslationsForSubKeys = ({parentKey}) => {
    const result = {};

    // Collect all unique sub-keys from ALL locales (not just the first one)
    const allSubKeys = new Set();
    Object.values(languagesMap).forEach(locale => {
        const parentObject = get(locale, parentKey);
        if (parentObject && typeof parentObject === 'object') {
            Object.keys(parentObject).forEach(key => allSubKeys.add(key));
        }
    });

    // Helper to add translations to a set
    const addTranslations = (translations, value) => {
        if (Array.isArray(value)) {
            value.forEach(v => {
                if (v && typeof v === 'string') {
                    translations.add(v.trim());
                    translations.add(v.trim().toLowerCase());
                }
            });
        } else if (value && typeof value === 'string') {
            translations.add(value.trim());
            translations.add(value.trim().toLowerCase());
        }
    };

    // For each sub-key, collect translations from all languages
    allSubKeys.forEach(subKey => {
        // Skip alias keys - they'll be merged into their base key
        if (subKey.endsWith('Aliases')) {
            return;
        }

        const fullKey = `${parentKey}.${subKey}`;
        const aliasKey = `${parentKey}.${subKey}Aliases`;
        const translations = new Set();

        Object.values(languagesMap).forEach(locale => {
            // Add main field translations
            const value = get(locale, fullKey);
            addTranslations(translations, value);

            // Add alias translations (merge into the base field)
            const aliasValue = get(locale, aliasKey);
            addTranslations(translations, aliasValue);
        });

        if (translations.size > 0) {
            result[subKey] = Array.from(translations);
        }
    });

    return result;
};
// Create Context
export const I18nReactContext = createContext(
    {
        language: defaultLanguage,
        i18n: new MDText(languagesMap[defaultLanguage]),
        switchLanguage: ({newSelectedLanguage}) => {
        },
        isRtl: false
    }
);

// Context Provider Component
export const I18nReactContextProvider = ({children}) => {
    let userContext = useContext(UserReactContext)
    const [language, setLanguage] = useState(defaultLanguage); // Default language
    const [i18n, setI18n] = useState(null);

    /**
     * Gets the initial language from localStorage or user preferences
     * 
     * @returns {string} The initial language code
     */
    const getInitialLanguage = () => {
        try {
            // When a user is logged in, their saved preference is the ONLY source of truth.
            // weezbee_login_language is a single per-device key shared by the super-admin and
            // any impersonated user, so reading it while logged in leaks the language from one
            // session into another (e.g. an impersonated user's choice bleeding back to the SA).
            // A logged-in user with no saved language therefore falls back to the default - never
            // to localStorage. localStorage is used only on the pre-login screen (no user).
            if (userContext.user && userContext.getPreferences) {
                const userLang = get(userContext.getPreferences({attributes: ['language']}), 'language');
                const resolved = isValidLanguage(userLang) ? userLang : defaultLanguage;
                console.log('Using user preference language:', resolved);
                return resolved;
            }

            // No logged-in user (e.g. login page): use the language picked on the login screen.
            const loginLanguage = localStorage.getItem('weezbee_login_language');
            if (isValidLanguage(loginLanguage)) {
                console.log('Using localStorage language:', loginLanguage);
                return loginLanguage;
            }

        } catch (error) {
            console.error('Error getting initial language:', error);
        }
        return defaultLanguage;
    };

    /**
     * Switches the application language and updates user preferences if applicable.
     *
     * @param {object} params - The function parameters container object.
     * @param {string} params.newSelectedLanguage - The new language code to switch to (e.g., 'enUS', 'heIL').
     * @returns {Promise<void>} A promise that resolves when the language switch and user update (if any) are complete.
     */
    const switchLanguage = async ({newSelectedLanguage}) => {
        // Validate the new language first
        if (!isValidLanguage(newSelectedLanguage)) {
            console.warn(`Invalid language '${newSelectedLanguage}' provided. Using default language.`);
            newSelectedLanguage = defaultLanguage;
        }

        if (language !== newSelectedLanguage) {
            try {
                setLanguage(newSelectedLanguage); // Update the state
                setI18n(new MDText(languagesMap[newSelectedLanguage]));
                // Save to localStorage immediately for login page
                localStorage.setItem('weezbee_login_language', newSelectedLanguage);
            } catch (error) {
                console.error('Error switching language:', error);
                // Fallback to default language if something goes wrong
                setLanguage(defaultLanguage);
                setI18n(new MDText(languagesMap[defaultLanguage]));
            }
        }

        let userLanguage = getSafeLanguage(get(userContext.getPreferences({attributes: ['language']}), 'language'))
        if (userContext.isUserValid() && userLanguage !== newSelectedLanguage) {
            try {
                // Call API to update user language preference
                await changeUserLanguage({language: newSelectedLanguage});

                userContext.changeUserLanguage({language: newSelectedLanguage});
                
                console.log('Successfully updated user language to:', newSelectedLanguage);
            } catch (apiError) {
                console.error('Failed to update user language preference:', apiError);
            }
        }
    };
    
    useEffect(() => {
        if(!i18n) {
            const initialLanguage = getInitialLanguage();
            
            setI18n(new MDText(languagesMap[initialLanguage]))
            setLanguage(initialLanguage);
        }
    }, [i18n])
    
    useEffect(() => {
        if(userContext.user){
            try {
                let userLang = getSafeLanguage(get(userContext.getPreferences({attributes: ['language']}), 'language'))
                if (userContext.isUserValid() && userLang !== language) {
                    // Adopt the active user's saved language. Passing userLang (not the current
                    // app language) is what makes a user switch show THAT user's language, and it
                    // also prevents switchLanguage from overwriting their stored preference - the
                    // target now equals what is already saved for them, so no DB write happens.
                    switchLanguage({newSelectedLanguage: userLang});
                }
            } catch (error) {
                console.error('Error handling user language preferences:', error);
                switchLanguage({newSelectedLanguage: defaultLanguage});
            }
        } 
    }, [userContext.user])
    
    const isRtl = isLanguageRtl({language})
    
    if(!i18n){
        return <LoadingSpinner/>
    }
    return <I18nReactContext.Provider value={{language, switchLanguage, i18n, isRtl}}>
        {children}
    </I18nReactContext.Provider>;
};

/**
 * Higher-Order Component that provides i18n context to class components
 * @param {React.Component} WrappedComponent - The component to wrap with i18n context
 * @returns {React.Component} The wrapped component with i18n context
 */
export const withI18nContext = (WrappedComponent) => {
    return function WithI18nContextComponent(props) {
        return (
            <I18nReactContext.Consumer>
                {(context) => <WrappedComponent {...props} {...context} />}
            </I18nReactContext.Consumer>
        );
    };
};
